/**
 * @krisspy-file
 * @type page
 * @name "LoginScreen"
 * @title "Login Screen"
 * @description "Authentication page for rental shop managers and staff"
 * @routes ["/login"]
 * @layout "AuthLayout"
 * @flowName "authentication"
 */

import { Mail, Lock, LogIn, HelpCircle, AlertCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

export default function LoginScreen() {
  const navigate = useNavigate();
  const { signIn, user, profile, loading } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if already logged in (wait for profile to decide where)
  useEffect(() => {
    if (!loading && user) {
      if (profile && !profile.password_set) {
        navigate('/setup-password');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, profile, loading, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Authenticate with Supabase
    const { error: signInError } = await signIn(email, password);

    if (signInError) {
      setError(signInError.message || t('auth.failedSignIn'));
      setIsLoading(false);
    } else {
      // Success - user will be redirected by useEffect
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-xl">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-primary mb-sm">{t('auth.welcomeBack')}</h2>
        <p className="text-secondary text-sm">{t('auth.signInDesc')}</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-md flex items-start gap-md">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-800 font-medium">{t('auth.authError')}</p>
            <p className="text-sm text-red-600 mt-xs">{error}</p>
          </div>
        </div>
      )}

      {/* Login Form */}
      <form onSubmit={handleLogin} className="space-y-lg">
        {/* Email Input */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-sm text-primary">
            {t('auth.emailAddress')}
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="manager@rental.com"
              className="input pl-11"
              required
              data-testid="login-form-email-input"
            />
          </div>
        </div>

        {/* Password Input */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium mb-sm text-primary">
            {t('auth.password')}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.enterPassword')}
              className="input pl-11"
              required
              data-testid="login-form-password-input"
            />
          </div>
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-sm cursor-pointer">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 rounded border-primary accent-blue cursor-pointer"
            />
            <span className="text-sm text-primary">{t('auth.rememberMe')}</span>
          </label>
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="text-sm text-blue hover:text-blue-hover transition-colors"
          >
            {t('auth.forgotPassword')}
          </button>
        </div>

        {/* Login Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn btn-primary w-full"
          data-testid="login-form-submit-button"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {t('auth.signingIn')}
            </>
          ) : (
            <>
              <LogIn className="w-4 h-4" />
              {t('auth.signIn')}
            </>
          )}
        </button>
      </form>

      {/* Support Link */}
      <div className="pt-lg border-t border-border">
        <button
          type="button"
          onClick={() => window.open('mailto:support@rental.com', '_blank')}
          className="flex items-center justify-center gap-sm text-sm text-secondary hover:text-primary transition-colors w-full"
        >
          <HelpCircle className="w-4 h-4" />
          {t('auth.needHelp')}
        </button>
      </div>
    </div>
  );
}
